import { ApiService } from '../apiService'
import type {
  UserProfileDto,
  UserAccountDto,
  UpdateUserProfileRequest,
  PasswordResetRequest,
  NotificationPreferencesDto,
  UpdateNotificationPreferencesRequest,
  UpdateUserPreferencesRequest,
  MfaStatusDto,
  MfaSetupDto,
  MfaEnableRequest,
  MfaVerifyRequest,
  MfaBackupCodesDto,
  UserSessionDto,
  DeleteSessionsDto,
  DeleteSessionsResponseDto,
  HomeLocationDto,
  UpdateHomeLocationRequest,
  UserSettingsDto,
  SettingsApiResponse,
  UserNotificationSettingsDto,
  UserPrivacySettingsDto,
  UpdateChatAgeRestrictionRequest,
  ManagedChildAccountDto,
  ParentAccountDto,
  CreateParentalLinkRequest,
  CreateParentalLinkResponse,
  PendingParentalLinkDto,
  PendingOutboundLinkDto,
  ConfirmParentalLinkRequest,
  SetChildPrivacySettingsRequest,
} from '../../types/settings'

export const settingsApi = {
  // ====================
  // USER PROFILE
  // ====================

  // There is no /api/user-settings controller — the whole namespace 404s. These pointed at it
  // and were repointed on the Website in a57f6d0; this app kept the broken copies, which only
  // started mattering when it began deploying.

  // Get user profile — MeController, [HttpGet("profile")]
  getUserProfile: () => ApiService.get<UserProfileDto>('/api/me/profile'),

  // Update user profile — MeController exposes POST, not PUT
  updateUserProfile: (data: UpdateUserProfileRequest) =>
    ApiService.post<SettingsApiResponse<UserProfileDto>>('/api/me/profile', data),

  /**
   * Send the signed-in user a password reset code.
   *
   * The same endpoint the signed-out flow uses — there is no separate authenticated one, and
   * routing it through auth means the code, expiry and rate limit are the ones already tested.
   */
  requestPasswordReset: (data: PasswordResetRequest) =>
    ApiService.post<SettingsApiResponse<void>>('/api/auth/forgot-password', {
      ...data,
      method: 'email',
    }),

  // DEAD: no /api/*/verify-email route exists. Unused; left visible rather than deleted so the
  // gap is findable if the flow is ever built.
  verifyEmail: (token: string) =>
    ApiService.post<SettingsApiResponse<void>>(`/api/user-settings/verify-email/${token}`),

  // DEAD: no /api/*/verify-phone route on this path. Registration has its own
  // (AuthController, register/verify-phone). Unused here.
  verifyPhone: (code: string) =>
    ApiService.post<SettingsApiResponse<void>>('/api/user-settings/verify-phone', { code }),

  // ====================
  // NOTIFICATION PREFERENCES
  // ====================

  // Get notification preferences
  getNotificationPreferences: () =>
    ApiService.get<NotificationPreferencesDto>('/api/me/settings/notifications'),

  // Update notification preferences
  updateNotificationPreferences: (data: UpdateNotificationPreferencesRequest) =>
    ApiService.put<SettingsApiResponse<NotificationPreferencesDto>>(
      '/api/me/settings/notifications',
      data
    ),

  // New notification settings endpoints (from API update)
  // Get user notification settings
  getUserNotificationSettings: () =>
    ApiService.get<UserNotificationSettingsDto>('/api/me/settings/notifications'),

  // Update user notification settings
  updateUserNotificationSettings: (data: UserNotificationSettingsDto) =>
    ApiService.put<UserNotificationSettingsDto>('/api/me/settings/notifications', data),

  // ====================
  // USER PREFERENCES
  // ====================

  // Get user preferences (theme, language, etc.)
  // Note: Preferences are now part of the user account
  getUserPreferences: async () => {
    const account = await ApiService.get<UserAccountDto>('/api/me/account')
    return {
      theme: (account.theme || 'System') as any,
      language: (account.language || 'en') as any,
      measurementUnit: account.measurementUnit ?? 1,
      showLocationOnProfile: account.showLocationOnProfile ?? false,
    }
  },

  // Update user preferences
  // Note: Updates the user account with preference data
  updateUserPreferences: async (data: UpdateUserPreferencesRequest) => {
    const account = await ApiService.get<UserAccountDto>('/api/me/account')
    const updatedAccount = await ApiService.post<UserAccountDto>('/api/me/account', {
      ...account,
      theme: data.theme,
      language: data.language,
      measurementUnit: data.measurementUnit,
      showLocationOnProfile: data.showLocationOnProfile,
    })
    return {
      success: true,
      data: {
        theme: updatedAccount.theme as any,
        language: updatedAccount.language as any,
        measurementUnit: updatedAccount.measurementUnit ?? 1,
        showLocationOnProfile: updatedAccount.showLocationOnProfile ?? false,
      },
    }
  },

  // ====================
  // MULTI-FACTOR AUTHENTICATION (MFA)
  // ====================

  // Get MFA status
  getMfaStatus: () => ApiService.get<MfaStatusDto>('/api/Mfa/status'),

  // Setup MFA (get QR code and secret)
  setupMfa: () => ApiService.post<MfaSetupDto>('/api/Mfa/setup'),

  // Enable MFA (verify and activate)
  enableMfa: (data: MfaEnableRequest) =>
    ApiService.post<void>('/api/Mfa/enable', {
      code: data.verificationCode,
      rememberDevice: false,
    }),

  // Disable MFA
  disableMfa: (data: MfaVerifyRequest) =>
    ApiService.post<void>('/api/Mfa/disable', {
      code: data.code,
      rememberDevice: false,
    }),

  // Regenerate backup codes
  // Needs a current authenticator code: backup codes are a way past two-step sign-in.
  regenerateMfaBackupCodes: (code: string) =>
    ApiService.post<MfaBackupCodesDto>('/api/Mfa/regenerate-backup-codes', { code }),

  // ====================
  // USER SESSIONS
  // ====================

  // Get all active sessions
  getUserSessions: () => ApiService.get<UserSessionDto[]>('/api/me/sessions'),

  // Revoke a specific session
  revokeSession: (data: DeleteSessionsDto) =>
    ApiService.delete<DeleteSessionsResponseDto>('/api/me/sessions', data),

  // Revoke all sessions except current
  revokeAllSessionsExceptCurrent: () =>
    ApiService.delete<DeleteSessionsResponseDto>('/api/me/sessions', { deleteAllExceptCurrent: true }),

  // ====================
  // HOME LOCATION
  // ====================

  // Get home location
  // /api/user-settings has no controller — this panel never loaded. The route now exists
  // at me/settings/home-location and returns the DTO directly, like the other settings
  // endpoints; there is no SettingsApiResponse envelope on the server.
  getHomeLocation: () => ApiService.get<HomeLocationDto>('/api/me/settings/home-location'),

  // Update home location
  updateHomeLocation: (data: UpdateHomeLocationRequest) =>
    ApiService.put<HomeLocationDto>('/api/me/settings/home-location', data),

  // ====================
  // LOCATION SHARING
  // ====================

  // Get location sharing settings from account
  getLocationSharingSettings: async () => {
    const account = await ApiService.get<UserAccountDto>('/api/me/account')

    // Try locationSettings first (new API), fallback to legacy fields
    const showHometown = account.locationSettings?.showHometown ?? account.showHometown ?? true
    const showLocationToFriends = account.locationSettings?.locShare_Friends ?? account.showLocationToFriends ?? false
    const showLocationToGroups = account.locationSettings?.locShare_Group ?? false
    const showLocationToAdventureParticipants =
      account.locationSettings?.showLocationToAdventureParticipants ??
      account.showLocationToAdventureParticipants ?? false

    return {
      showHometown,
      showLocationToFriends,
      showLocationToGroups,
      showLocationToAdventureParticipants,
    }
  },

  // Update location sharing settings
  updateLocationSharingSettings: async (data: {
    showHometown: boolean
    showLocationToFriends: boolean
    showLocationToGroups: boolean
    showLocationToAdventureParticipants: boolean
  }) => {
    const account = await ApiService.get<UserAccountDto>('/api/me/account')

    // Build the locationSettings object with updated values
    const locationSettings = {
      ...(account.locationSettings || {}),
      showHometown: data.showHometown,
      locShare_Friends: data.showLocationToFriends,
      locShare_Group: data.showLocationToGroups,
      showLocationToAdventureParticipants: data.showLocationToAdventureParticipants,
    }

    const updatedAccount = await ApiService.post<UserAccountDto>('/api/me/account', {
      ...account,
      locationSettings,
    })

    return {
      success: true,
      data: {
        showHometown: updatedAccount.locationSettings?.showHometown ?? true,
        showLocationToFriends: updatedAccount.locationSettings?.locShare_Friends ?? false,
        showLocationToGroups: updatedAccount.locationSettings?.locShare_Group ?? false,
        showLocationToAdventureParticipants: updatedAccount.locationSettings?.showLocationToAdventureParticipants ?? false,
      },
    }
  },

  // ====================
  // BILLING ADDRESS & HOME LOCATION
  // ====================

  // Get billing address and home location
  getBillingAddress: async () => {
    const account = await ApiService.get<UserAccountDto>('/api/me/account')

    // Try to get from locationSettings first (new API structure), fallback to legacy fields
    const homeLatitude = account.locationSettings?.homeLatitude ?? account.homeLatitude ?? null
    const homeLongitude = account.locationSettings?.homeLongitude ?? account.homeLongitude ?? null
    const locationSharingMinHideDistanceMiles =
      account.locationSettings?.locationSharingMinHideDistanceMiles ??
      account.locationSharingMinHideDistanceMiles ??
      null

    return {
      billingAddress: account.billingAddress || {
        addressLine1: '',
        addressLine2: '',
        city: '',
        stateOrProvince: '',
        postalCode: '',
        country: '',
      },
      homeLatitude,
      homeLongitude,
      locationSharingMinHideDistanceMiles,
    }
  },

  // Update billing address and home location
  updateBillingAddress: async (data: {
    billingAddress: {
      addressLine1: string
      addressLine2: string
      city: string
      stateOrProvince: string
      postalCode: string
      country: string
    }
    homeLatitude: number | null
    homeLongitude: number | null
    locationSharingMinHideDistanceMiles: number | null
  }) => {
    const account = await ApiService.get<UserAccountDto>('/api/me/account')

    // Build the locationSettings object with updated values
    const locationSettings = {
      ...(account.locationSettings || {}),
      homeLatitude: data.homeLatitude,
      homeLongitude: data.homeLongitude,
      locationSharingMinHideDistanceMiles: data.locationSharingMinHideDistanceMiles,
      homeLocationLastUpdated: new Date().toISOString(),
    }

    await ApiService.post<UserAccountDto>('/api/me/account', {
      ...account,
      billingAddress: data.billingAddress,
      locationSettings,
    })
  },

  // ====================
  // COMBINED SETTINGS
  // ====================

  // Get all user settings at once (optional optimization endpoint)
  getAllUserSettings: () => ApiService.get<UserSettingsDto>('/api/user-settings'),

  // ====================
  // PRIVACY SETTINGS
  // ====================

  // Get privacy settings (chat age restrictions)
  getPrivacySettings: () => ApiService.get<UserPrivacySettingsDto>('/api/me/settings/privacy'),

  // Update chat age restrictions
  updateChatAgeRestriction: (data: UpdateChatAgeRestrictionRequest) =>
    ApiService.put<UserPrivacySettingsDto>('/api/me/settings/privacy/chat-age-restriction', data),

  // ====================
  // PARENTAL CONTROLS
  // ====================

  // Get managed child accounts (for parents)
  getManagedChildren: () => ApiService.get<ManagedChildAccountDto[]>('/api/me/parental/children'),

  // Get parent accounts (for children)
  getParentAccounts: () => ApiService.get<ParentAccountDto[]>('/api/me/parental/parents'),

  // Create a parental link request (parent initiates)
  createParentalLink: (data: CreateParentalLinkRequest) =>
    ApiService.post<CreateParentalLinkResponse>('/api/me/parental/link', data),

  // Get pending link requests (for children to confirm)
  getPendingLinkRequests: () => ApiService.get<PendingParentalLinkDto[]>('/api/me/parental/requests'),

  // Confirm a parental link (child confirms with verification code)
  confirmParentalLink: (data: ConfirmParentalLinkRequest) =>
    ApiService.post<void>('/api/me/parental/confirm', data),

  // Revoke a parental link
  revokeParentalLink: (linkId: string) =>
    ApiService.delete<void>(`/api/me/parental/link/${linkId}`),

  // Update child's privacy settings (parent manages child)
  updateChildPrivacySettings: (data: SetChildPrivacySettingsRequest) =>
    ApiService.put<UserPrivacySettingsDto>('/api/me/parental/child-settings', data),

  // Get pending outbound link requests (for parents to see their sent requests)
  getPendingOutboundRequests: () =>
    ApiService.get<PendingOutboundLinkDto[]>('/api/me/parental/pending'),

  // Cancel/recall a pending outbound request (parent cancels)
  cancelPendingRequest: (linkId: string) =>
    ApiService.delete<void>(`/api/me/parental/pending/${linkId}`),

  // Decline a link request (child declines)
  declineParentalLink: (linkId: string) =>
    ApiService.post<void>(`/api/me/parental/decline/${linkId}`),
}
